/**
 * @file useModerationData.js
 * @brief Loads moderation dashboard data, including listing queues, users, and preview images.
 */
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Maintain the paginated data displayed in the moderation dashboard.
 * @param {Object} params Hook parameters.
 * @param {Object} params.api API client used for moderation requests.
 * @param {string} params.accessToken Bearer token used when fetching protected listing images.
 * @param {string} params.activeTab Currently selected moderation tab.
 * @param {number} params.page Current pagination page.
 * @param {string} params.search Current search query.
 * @returns {Object} Moderation dashboard data and mutation helpers.
 */
export default function useModerationData({ api, accessToken, activeTab, page, search }) {
    const [loading, setLoading] = useState(true);
    const [count, setCount] = useState(0);
    const [next, setNext] = useState(null);
    const [previous, setPrevious] = useState(null);

    const [listings, setListings] = useState([]);
    const [users, setUsers] = useState([]);
    const [imagesMap, setImagesMap] = useState({});

    const latestRequestIdRef = useRef(0);
    const objectUrlsRef = useRef([]);

    const revokeObjectUrls = useCallback(() => {
        objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
        objectUrlsRef.current = [];
    }, []);

    const fetchListings = useCallback(
        async (pageNumber = 1, query = "", status = "pend_app") => {
            if (!accessToken) return;

            const requestId = latestRequestIdRef.current + 1;
            latestRequestIdRef.current = requestId;
            setLoading(true);

            try {
                const res = await api.get(`/listings/?status=${status}&page=${pageNumber}&search=${query}`);
                const data = res.data;

                const map = {};
                const newObjectUrls = [];

                if (status === "pend_app") {
                    await Promise.all(
                        data.results.map(async (listing) => {
                            if (listing.images?.length > 0) {
                                const imgRes = await fetch(listing.images[0].url, {
                                    headers: { Authorization: `Bearer ${accessToken}` },
                                });
                                if (!imgRes.ok) {
                                    map[listing.id] = null;
                                    return;
                                }
                                const blob = await imgRes.blob();
                                const objectUrl = URL.createObjectURL(blob);
                                map[listing.id] = objectUrl;
                                newObjectUrls.push(objectUrl);
                            } else {
                                map[listing.id] = null;
                            }
                        })
                    );
                }

                if (requestId !== latestRequestIdRef.current) {
                    newObjectUrls.forEach((url) => URL.revokeObjectURL(url));
                    return;
                }

                setListings(data.results);
                setUsers([]);
                setCount(data.count);
                setNext(data.next);
                setPrevious(data.previous);

                revokeObjectUrls();
                objectUrlsRef.current = newObjectUrls;
                setImagesMap(status === "pend_app" ? map : {});
            } catch (err) {
                if (requestId !== latestRequestIdRef.current) return;
                console.error("Failed to fetch listings", err);
            } finally {
                if (requestId === latestRequestIdRef.current) {
                    setLoading(false);
                }
            }
        },
        [api, accessToken, revokeObjectUrls]
    );

    const fetchUsers = useCallback(
        async (pageNumber = 1, query = "") => {
            if (!accessToken) return;

            const requestId = latestRequestIdRef.current + 1;
            latestRequestIdRef.current = requestId;
            setLoading(true);

            try {
                const res = await api.get(`/userprofiles/?page=${pageNumber}&search=${query}`);
                const data = res.data;

                if (requestId !== latestRequestIdRef.current) return;

                setUsers(data.results);
                setListings([]);
                setCount(data.count);
                setNext(data.next);
                setPrevious(data.previous);

                revokeObjectUrls();
                setImagesMap({});
            } catch (err) {
                if (requestId !== latestRequestIdRef.current) return;
                console.error("Failed to fetch users", err);
            } finally {
                if (requestId === latestRequestIdRef.current) {
                    setLoading(false);
                }
            }
        },
        [api, accessToken, revokeObjectUrls]
    );

    const refreshCurrent = useCallback(async () => {
        if (activeTab === "overview") {
            setListings([]);
            setUsers([]);
            setCount(0);
            setNext(null);
            setPrevious(null);
            setLoading(false);
            return;
        }

        if (activeTab === "pending") {
            await fetchListings(page, search, "pend_app");
            return;
        }
        if (activeTab === "rejected") {
            await fetchListings(page, search, "rejected");
            return;
        }
        await fetchUsers(page, search);
    }, [activeTab, page, search, fetchListings, fetchUsers]);

    const updateListingStatus = useCallback(
        async (ids, status) => {
            await Promise.all(ids.map((id) => api.patch(`/listings/${id}/`, { status })));
            await refreshCurrent();
        },
        [api, refreshCurrent]
    );

    useEffect(() => {
        if (!accessToken) return;
        refreshCurrent();
    }, [accessToken, refreshCurrent]);

    useEffect(() => () => revokeObjectUrls(), [revokeObjectUrls]);

    return {
        loading,
        count,
        next,
        previous,
        listings,
        users,
        imagesMap,
        refreshCurrent,
        updateListingStatus,
    };
}
